import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  SortingState,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp, ArrowUpDown } from 'lucide-react';

import { fetchPaginatedProducts, deleteProduct, ITEMS_PER_PAGE } from '../AddProduct/api';
import { Product, Script } from '../AddProduct/types';
import { formatCurrency } from '../ViewProduct/utils';
import { GlobalTable, HeaderLine, Td, Tr, FooterTr } from '../../../../components/styles/table-style';
import LoadMoreButton from '../../../../components/LoadMoreButton';
import {
  Th,
  TableContainer,
  OptionsImage,
  ConfigMenuWrapper,
  ConfigMenuButton,
  ColumnMenu,
  ColumnMenuTitle,
  ColumnMenuList,
  ColumnMenuItem,
  ThContent,
  StyledSelect
} from './style';
import { Button } from '../AddProduct/style';
import listIcon from '../../../../assets/details.svg';
import editIcon from '../../../../assets/edit-pen.svg';
import trashIcon from '../../../../assets/trash.svg';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import ConfirmationModal from '../../../../components/ModalConfirmation';
import TableRow from './TableRow';
import { useLoadMore } from '../../../../contexts/LoadMoreContext';

interface ProductListItem extends Omit<Product, 'scripts' | 'quantity' | 'createdAt' | 'updatedAt'> {
  scripts: Script[];
  selectedScriptId?: number;
}

const ProductFinalsTable: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setFeedback } = React.useContext(FeedbackContext);
  const { refresh, appliedFilters, search } = useOutletContext<{ refresh: boolean; appliedFilters: any[], search: string }>();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [showColumnMenu, setShowColumnMenu] = useState(false);

const searchFilter = appliedFilters.find(filter => filter.key === 'name');

const searchTerm = search || searchFilter?.value || '';

const otherFilters = appliedFilters.filter(filter => filter.key !== 'name');

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch
  } = useInfiniteQuery({
    queryKey: ['products', searchTerm, otherFilters],
    queryFn: ({ pageParam = 1 }) => {
      return fetchPaginatedProducts({
        pageParam,
        search: searchTerm,
        filters: otherFilters.length > 0 ? otherFilters : undefined
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || !lastPage.data) return undefined;
      const totalFetched = allPages.length * ITEMS_PER_PAGE;
      if (totalFetched < lastPage.total) {
        return allPages.length + 1;
      }
      return undefined;
    },
    select: (queryData) => queryData.pages.flatMap((page) => page.data ?? []),
  });

  const products = data || [];
  const { setHasNextPage, setIsFetchingNextPage, setFetchNextPageFunction } = useLoadMore();

  useEffect(() => {
    refetch();
  }, [refresh, refetch]);

  useEffect(() => {
    setHasNextPage(!!hasNextPage);
  }, [hasNextPage, setHasNextPage]);

  useEffect(() => {
    setIsFetchingNextPage(isFetchingNextPage);
  }, [isFetchingNextPage, setIsFetchingNextPage]);

  const memoizedFetchNextPage = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  useEffect(() => {
    setFetchNextPageFunction(memoizedFetchNextPage);
  }, [memoizedFetchNextPage, setFetchNextPageFunction]);

  const columns = useMemo<ColumnDef<ProductListItem>[]>(
    () => [
      { accessorKey: 'name', header: 'Produto' },
      { accessorKey: 'norm', header: 'Norma' },
      {
        accessorKey: 'script',
        header: 'Script',
        cell: () => null,
        enableSorting: false,
      },
      {
        accessorKey: 'items',
        header: 'Itens',
        cell: () => null,
        enableSorting: false,
      },
      {
        accessorKey: 'deadline',
        header: 'Prazo (dias)',
        cell: () => null,
        accessorFn: (row) => {
          const selectedScript = row.scripts?.find(s => s.id === row.selectedScriptId) || row.scripts?.[0];
          if (!selectedScript) return 0;
          const additional = selectedScript.additionalDeadline || 0;
          const servicesTime = selectedScript.totalDeliveryTime || 0;
          return additional + servicesTime;
        },
      },
      {
        accessorKey: 'cost',
        header: 'Custo (últ.)',
        cell: () => null,
        accessorFn: (row) => {
          const selectedScript = row.scripts?.find(s => s.id === row.selectedScriptId) || row.scripts?.[0];
          return selectedScript?.cost || 0;
        },
      },
      {
        accessorKey: 'price',
        header: 'Preço (últ)',
        cell: () => null,
        accessorFn: (row) => {
          const selectedScript = row.scripts?.find(s => s.id === row.selectedScriptId) || row.scripts?.[0];
          const cost = selectedScript?.cost || 0;
          return cost * 1.3;
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', paddingRight: '1rem' }}>
            <OptionsImage src={listIcon} alt="Ver detalhes" onClick={() => handleView(row.original.id!)} title="Ver detalhes" />
            <OptionsImage src={editIcon} alt="Editar" onClick={() => handleEdit(row.original.id!)} title="Editar" />
            <OptionsImage src={trashIcon} alt="Excluir" onClick={() => handleDeleteClick(row.original.id!, row.original.name)} title="Excluir" />
          </div>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: products,
    columns,
    state: {
      sorting,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const [deleteModal, setDeleteModal] = useState({ isOpen: false, productId: null as number | null, productName: '' });

  const { mutate: removeProduct } = useMutation({
    mutationFn: (id: number) => deleteProduct(String(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setFeedback({ message: 'Produto excluído com sucesso!', isOpen: true, statusOk: true });
      setDeleteModal({ isOpen: false, productId: null, productName: '' });
    },
    onError: (error: Error) => {
      setFeedback({ message: `Erro ao excluir produto: ${error.message}`, isOpen: true, statusOk: false });
    },
  });

  const handleView = (productId: number) => navigate(`/panel/product-final/${productId}`);
  const handleEdit = (productId: number) => navigate(`/panel/product-final/edit/${productId}`);
  const handleDeleteClick = (productId: number, productName: string) => {
    setDeleteModal({ isOpen: true, productId, productName });
  };
  const handleConfirmDelete = () => {
    if (deleteModal.productId) removeProduct(deleteModal.productId);
  };
  const handleCancelDelete = () => setDeleteModal({ isOpen: false, productId: null, productName: '' });

  if (isLoading) return <div style={{ textAlign: 'center', padding: '2rem' }}>Carregando...</div>;
  if (isError) return <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>Erro ao carregar produtos: {error.message}</div>;

  return (
    <>
      <TableContainer>
        <ConfigMenuWrapper>
          <ConfigMenuButton onClick={() => setShowColumnMenu(v => !v)} title="Selecionar colunas">
            ⚙️
          </ConfigMenuButton>
          {showColumnMenu && (
            <ColumnMenu>
              <ColumnMenuTitle>Selecionar Colunas</ColumnMenuTitle>
              <ColumnMenuList>
                {table.getAllLeafColumns().map(column => {
                  if (column.id === 'actions') return null;
                  return (
                    <ColumnMenuItem key={column.id}>
                      <input
                        {...{
                          type: 'checkbox',
                          checked: column.getIsVisible(),
                          onChange: column.getToggleVisibilityHandler(),
                        }}
                      />
                      <label>{column.columnDef.header as string}</label>
                    </ColumnMenuItem>
                  );
                })}
              </ColumnMenuList>
            </ColumnMenu>
          )}
        </ConfigMenuWrapper>
        <GlobalTable>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <HeaderLine key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <Th
                    key={header.id}
                    $sortable={header.column.getCanSort()}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <ThContent>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {(() => {
                        const sortDirection = header.column.getIsSorted() as string;
                        if (sortDirection === 'asc') return <ChevronUp size={14} />;
                        if (sortDirection === 'desc') return <ChevronDown size={14} />;
                        if (header.column.getCanSort()) return <ArrowUpDown size={14} style={{ opacity: 0.3 }} />;
                        return null;
                      })()}
                    </ThContent>
                  </Th>
                ))}
              </HeaderLine>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map(row => (
                <TableRow
                  key={row.id}
                  row={row}
                  handleView={handleView}
                  handleEdit={handleEdit}
                  handleDeleteClick={handleDeleteClick}
                />
              ))
            ) : (
              <Tr>
                <td colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem' }}>
                  Nenhum produto encontrado.
                </td>
              </Tr>
          )}
        </tbody>
        <tfoot>
          <FooterTr>
            <Td colSpan={columns.length} style={{ textAlign: 'center', padding: '1rem 0', background: 'transparent' }}>
              <LoadMoreButton
                currentCount={products.length}
                onLoadMore={() => fetchNextPage()}
                isLoading={isFetchingNextPage}
                hasMore={!!hasNextPage}
              />
            </Td>
          </FooterTr>
        </tfoot>
      </GlobalTable>      </TableContainer>

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        title="Confirmar Exclusão"
        message={
          <div>
            <p>Tem certeza que deseja excluir o produto: &quot;{deleteModal.productName}&quot;?</p>
            <p>Esta ação não pode ser desfeita.</p>
          </div>
        }
        actions={[
          { label: 'Cancelar', onClick: handleCancelDelete, variant: 'outline' },
          { label: 'Excluir', onClick: handleConfirmDelete, variant: 'danger' },
        ]}
      />
    </>
  );
};

export default ProductFinalsTable; 